import { attachmentDownloadName } from '@felix/domain';
import { ActionIcon, Pagination, Tabs, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconCalendarEvent,
  IconCategory2,
  IconDownload,
  IconEye,
  IconFile,
  IconInfoCircle,
} from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import type { MenuScope } from '../../menu.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Documents.module.css';
import { folderLabel, formatFileSize, revisionBare, revisionText } from './library.js';
import { FileKindIcon, RevisionBadge } from './parts.js';
import type { LibraryDetail, LibraryRevision, LibrarySection } from './types.js';

export type DetailsTab = 'view' | 'information' | 'history' | 'attachments';

/** Anın günü `GG.AA.YYYY`: gemide gemi saatiyle, ofiste tarayıcının saatiyle. */
export function instantDay(value: string | null | undefined, offsetMin?: number): string {
  const text = formatDateTime(value, offsetMin);
  return text === '—' ? text : text.slice(0, 10);
}

/** Yürürlük günü: ileri tarihliyse `effectiveOn` (gemi yerel günü), değilse yayın anı. */
export function effectiveDay(revision: LibraryRevision, offsetMin?: number): string {
  if (revision.effectiveOn) {
    const [y, m, d] = revision.effectiveOn.split('-');
    return `${d}.${m}.${y}`;
  }
  return instantDay(revision.publishedAt ?? revision.createdAt, offsetMin);
}

export interface DocumentDetailsPanelProps {
  section: LibrarySection;
  scope: MenuScope;
  detail: LibraryDetail;
  /** Önizlenen revizyon; yoksa `null` (dokümanın revizyonu yok). */
  revision: LibraryRevision | null;
  tab: DetailsTab;
  onTab: (tab: DetailsTab) => void;
  onPreviewRevision: (revisionId: string) => void;
  onDownloadFile: (file: { fileId: string; name: string }) => void;
  /** Önizlenen revizyonun dosyaları indirilebilir mi (gemide yalnız yürürlükteki). */
  canOutput: boolean;
  /** Gemi saati farkı (dakika); ofiste verilmez. */
  offsetMin?: number | undefined;
  /** Form View'ın önizleyicisi (ya da "henüz gemide değil" kartı). */
  viewer: ReactNode;
  /** Sekmelerin üstündeki şerit ("bu revizyonda ne değişti"; ofiste onay akışı). */
  notice?: ReactNode;
  /** History sekmesinde revizyon ağacının altı: formun kayıtları (M8b3; ADR 0042 §21). */
  historyFooter?: ReactNode;
  /** History satırının ek eylemi (ofiste yürürlükteki revizyonu geri çekme; Task 17). */
  historyAction?: ((revision: LibraryRevision) => ReactNode) | undefined;
  /**
   * History satırında durumun yanındaki işaret (gemi belgesi; M8a4e): gemide dosyanın buluta
   * yüklenmesi, ofiste dosyanın bulut durumu (ADR 0038 §14).
   */
  historyMarker?: ((revision: LibraryRevision) => ReactNode) | undefined;
  /**
   * Ek satırının dosya durumu ve indirilebilirliği (ofiste gemi belgesi: yalnız ofiste doğrulanmış
   * dosya indirilir; `FILE_NOT_UPLOADED`).
   */
  fileStatus?: ((fileId: string) => { available: boolean; note: ReactNode }) | undefined;
}

/**
 * "Form Details" kartı (gemi-ism-formlari.jpeg): sekmeler (Form View, Information, History,
 * Attachments; `role="tablist"`, ok tuşlarıyla). Form View'da altı hücreli bilgi şeridi ve
 * önizleyici; History'de dalın revizyonları (satır önizlenir; M8c ağacı aynı veriden çizer).
 */
export function DocumentDetailsPanel({
  section,
  scope,
  detail,
  revision,
  tab,
  onTab,
  onPreviewRevision,
  onDownloadFile,
  canOutput,
  offsetMin,
  viewer,
  notice,
  historyFooter,
  historyAction,
  historyMarker,
  fileStatus,
}: DocumentDetailsPanelProps) {
  const { t } = useTranslation();
  const labels = (key: string) => t(`documents.sections.${section}.${key}`);
  return (
    <Panel title={labels('details')} className={classes.details}>
      {notice}
      {/* Form View paneli `display:none` ile gizlenir (Mantine'in varsayılanı React <Activity>):
          önizleyicinin etkileri ve tutamağı başka sekmede de yaşar; baskı ve Open Document her
          sekmeden çalışır, dosya yeniden indirilmez (inceleme S1) */}
      <Tabs
        value={tab}
        keepMountedMode="display-none"
        onChange={(value) => value && onTab(value as DetailsTab)}
        className={classes.tabs}
        classNames={{ list: classes.tabList, tab: classes.tab, panel: classes.tabPanel }}
      >
        <Tabs.List>
          <Tabs.Tab value="view">{labels('view')}</Tabs.Tab>
          <Tabs.Tab value="information">{t('documents.tabs.information')}</Tabs.Tab>
          <Tabs.Tab value="history">{t('documents.tabs.history')}</Tabs.Tab>
          <Tabs.Tab value="attachments">{t('documents.tabs.attachments')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="view" keepMounted className={classes.viewPanel}>
          {revision ? (
            <InfoStrip
              section={section}
              detail={detail}
              revision={revision}
              offsetMin={offsetMin}
            />
          ) : null}
          {viewer}
        </Tabs.Panel>
        <Tabs.Panel value="information" keepMounted={false}>
          <InformationTab detail={detail} revision={revision} offsetMin={offsetMin} />
        </Tabs.Panel>
        <Tabs.Panel value="history" keepMounted={false}>
          <HistoryTab
            scope={scope}
            detail={detail}
            previewing={revision?.id ?? null}
            offsetMin={offsetMin}
            action={historyAction}
            marker={historyMarker}
            onPreview={(id) => {
              onPreviewRevision(id);
              onTab('view');
            }}
          />
          {historyFooter}
        </Tabs.Panel>
        <Tabs.Panel value="attachments" keepMounted={false}>
          <AttachmentsTab
            revision={revision}
            canOutput={canOutput}
            onDownload={onDownloadFile}
            fileStatus={fileStatus}
          />
        </Tabs.Panel>
      </Tabs>
    </Panel>
  );
}

/** Bilgi şeridi: Form No (büyük), Form Name, Category, File Type, Version, Updated. */
function InfoStrip({
  section,
  detail,
  revision,
  offsetMin,
}: {
  section: LibrarySection;
  detail: LibraryDetail;
  revision: LibraryRevision;
  offsetMin: number | undefined;
}) {
  const { t } = useTranslation();
  const labels = (key: string) => t(`documents.sections.${section}.${key}`);
  const category = detail.folderPath.at(-1)?.name ?? '—';
  // dt ve dd hücrenin doğrudan çocukları (dl > div > dt + dd); ikon dt'nin içinde, solda
  // Büyük ikon solda iki satırı kaplar (Form No, Updated); küçük ikon etiketin önünde (Category,
  // File Type), gemi-ism-formlari.jpeg bilgi şeridi
  const cell = (
    key: string,
    label: string,
    value: ReactNode,
    icon?: ReactNode,
    title?: string,
    inline = false,
  ) => (
    <div className={classes.stripCell} data-cell={key} data-icon={icon && !inline ? '' : undefined}>
      <dt>
        {icon ? (
          <span className={inline ? classes.stripLabelIcon : classes.stripIcon} aria-hidden>
            {icon}
          </span>
        ) : null}
        {label}
      </dt>
      <dd title={title}>{value}</dd>
    </div>
  );
  return (
    <dl className={classes.strip} data-info-strip>
      {cell(
        'number',
        labels('number'),
        <span className={classes.stripNumber}>{detail.docNo}</span>,
        <IconInfoCircle size={20} stroke={1.7} />,
      )}
      {cell('name', labels('name'), detail.title, undefined, detail.title)}
      {cell(
        'category',
        t('documents.fields.category'),
        category,
        <IconCategory2 size={13} stroke={1.8} />,
        category,
        true,
      )}
      {cell(
        'type',
        t('documents.fields.fileType'),
        <span className={classes.stripType}>
          <FileKindIcon kind={revision.fileKind} size={22} />
          {t(`documents.fileKinds.${revision.fileKind}`)}
        </span>,
        <IconFile size={13} stroke={1.8} />,
        undefined,
        true,
      )}
      {cell('version', t('documents.fields.version'), revisionBare(revision.revisionNo))}
      {cell(
        'updated',
        t('documents.fields.updated'),
        effectiveDay(revision, offsetMin),
        <IconCalendarEvent size={20} stroke={1.7} />,
      )}
    </dl>
  );
}

function InformationTab({
  detail,
  revision,
  offsetMin,
}: {
  detail: LibraryDetail;
  revision: LibraryRevision | null;
  offsetMin: number | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`documents.info.${key}`);
  const current = detail.current;
  const rows: [string, ReactNode][] = [
    [k('number'), detail.docNo],
    [k('title'), detail.title],
    [k('kind'), t(`documents.info.kinds.${detail.kind}`)],
    [k('folder'), detail.folderPath.map(folderLabel).join(' › ') || '—'],
    [k('scope'), t(`documents.info.scopes.${detail.scope}`)],
    [k('branch'), k('main')],
    [
      k('currentRevision'),
      current ? (
        revisionText(current.revisionNo)
      ) : (
        <span key="none" className={classes.muted}>
          {k('noCurrent')}
        </span>
      ),
    ],
    [k('effective'), revision ? effectiveDay(revision, offsetMin) : '—'],
    [k('preparedBy'), revision?.createdByName ?? '—'],
    [k('approvedBy'), revision?.approvedByName ?? '—'],
    [k('changeSummary'), revision?.changeSummary ?? '—'],
  ];
  return (
    <div className={classes.tabBody}>
      {detail.scheduled ? (
        <p className={classes.infoNote}>
          {t('documents.info.scheduled', {
            revision: revisionBare(detail.scheduled.revisionNo),
            date: detail.scheduled.effectiveOn.split('-').reverse().join('.'),
          })}
        </p>
      ) : null}
      <dl className={classes.facts}>
        {rows.map(([label, value]) => (
          <div key={label} className={classes.fact}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const HISTORY_PAGE = 10;

/**
 * Revizyon geçmişinde satırın katmanı (ADR 0116 §5): yürürlükte (`published`), açık (taslak, onay
 * bekleyen, onaylı, ileri tarihli) ya da geçmiş (yerine yenisi geçmiş, geri çekilmiş, vazgeçilmiş).
 */
export function revisionTier(state: LibraryRevision['state']): 'current' | 'open' | 'past' {
  if (state === 'published') return 'current';
  if (
    state === 'draft' ||
    state === 'pending_approval' ||
    state === 'approved' ||
    state === 'scheduled'
  )
    return 'open';
  return 'past';
}

function HistoryTab({
  scope,
  detail,
  previewing,
  offsetMin,
  action,
  marker,
  onPreview,
}: {
  scope: MenuScope;
  detail: LibraryDetail;
  previewing: string | null;
  offsetMin: number | undefined;
  action?: ((revision: LibraryRevision) => ReactNode) | undefined;
  marker?: ((revision: LibraryRevision) => ReactNode) | undefined;
  onPreview: (revisionId: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.history.${key}`, options ?? {});
  const [descending, setDescending] = useState(true);
  const [page, setPage] = useState(1);
  // Gemide yalnız gemiye inen (yayınlanmış) revizyonlar vardır; ofiste taslaklar da
  const sorted = useMemo(
    () =>
      [...detail.revisions].sort((a, b) => (descending ? b.seqNo - a.seqNo : a.seqNo - b.seqNo)),
    [detail.revisions, descending],
  );
  if (sorted.length === 0) {
    return (
      <div className={classes.tabBody}>
        <EmptyState title={k('empty')} />
      </div>
    );
  }
  const pages = Math.ceil(sorted.length / HISTORY_PAGE);
  const visible = sorted.slice((page - 1) * HISTORY_PAGE, page * HISTORY_PAGE);
  return (
    <div className={classes.tabBody}>
      <div className={classes.tableWrap}>
        <table className={classes.table} data-scope={scope}>
          <caption className={classes.srOnly}>{k('caption')}</caption>
          <thead>
            <tr>
              <th scope="col" aria-sort={descending ? 'descending' : 'ascending'}>
                <UnstyledButton
                  className={classes.sortButton}
                  onClick={() => setDescending((value) => !value)}
                >
                  {k('seq')}
                  {descending ? (
                    <IconArrowDown size={12} aria-hidden />
                  ) : (
                    <IconArrowUp size={12} aria-hidden />
                  )}
                </UnstyledButton>
              </th>
              <th scope="col">{k('revision')}</th>
              <th scope="col">{k('status')}</th>
              <th scope="col">{k('date')}</th>
              <th scope="col">{k('uploadedBy')}</th>
              <th scope="col">{k('approvedBy')}</th>
              <th scope="col">{k('summary')}</th>
              <th scope="col">
                <span className={classes.srOnly}>{k('preview', { revision: '' })}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((revision) => {
              const shown = revision.id === previewing;
              return (
                <tr
                  key={revision.id}
                  data-current={shown || undefined}
                  data-revision={revisionTier(revision.state)}
                >
                  <td className={classes.numeric}>{revision.seqNo}</td>
                  <td className={classes.revCell}>
                    {/* Revizyon hiyerarşisi (ADR 0116 §5): yürürlükteki dolu nokta ve kalın,
                        açık revizyon halkalı, geçmiştekiler soluk; noktalar ince çizgiyle bağlı */}
                    <span className={classes.revNode} aria-hidden />
                    <span className={classes.revNo}>{revisionBare(revision.revisionNo)}</span>
                  </td>
                  <td>
                    <span className={classes.historyMarker}>
                      <RevisionBadge state={revision.state} />
                      {marker?.(revision)}
                    </span>
                  </td>
                  <td className={`${classes.nowrap} ${classes.numericText}`}>
                    {instantDay(revision.publishedAt ?? revision.createdAt, offsetMin)}
                  </td>
                  <td>{revision.createdByName ?? '—'}</td>
                  <td>
                    {/* Kaptan kendi yüklemesini gerekçeli istisnayla onayladı (ADR 0038 §10) */}
                    {revision.selfApproved ? (
                      <span className={classes.selfApproved}>
                        {revision.approvedByName ?? '—'}
                        <Tooltip
                          label={t('documents.library.selfApprovedHint')}
                          withArrow
                          multiline
                          w={260}
                        >
                          <StatusBadge tone="info">
                            {t('documents.library.selfApproved')}
                          </StatusBadge>
                        </Tooltip>
                      </span>
                    ) : (
                      (revision.approvedByName ?? '—')
                    )}
                  </td>
                  <td className={classes.summaryCell} title={revision.changeSummary}>
                    {revision.changeSummary}
                  </td>
                  <td className={classes.actionCell}>
                    {action?.(revision)}
                    {shown ? (
                      <span className={classes.shownTag}>{k('previewing')}</span>
                    ) : (
                      <Tooltip
                        label={k('preview', { revision: revisionBare(revision.revisionNo) })}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          aria-label={k('preview', { revision: revisionBare(revision.revisionNo) })}
                          onClick={() => onPreview(revision.id)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 ? (
        <Pagination
          size="sm"
          total={pages}
          value={page}
          onChange={setPage}
          className={classes.pagination}
          getControlProps={(control) => ({ 'aria-label': t(`documents.page.${control}`) })}
        />
      ) : null}
    </div>
  );
}

function AttachmentsTab({
  revision,
  canOutput,
  onDownload,
  fileStatus,
}: {
  revision: LibraryRevision | null;
  canOutput: boolean;
  onDownload: (file: { fileId: string; name: string }) => void;
  fileStatus?: ((fileId: string) => { available: boolean; note: ReactNode }) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const attachments = revision?.attachments ?? [];
  if (!revision || attachments.length === 0) {
    return (
      <div className={classes.tabBody}>
        <EmptyState title={t('documents.attachments.empty')} />
      </div>
    );
  }
  return (
    <div className={classes.tabBody}>
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th scope="col">{t('documents.attachments.file')}</th>
              <th scope="col">{t('documents.attachments.role')}</th>
              <th scope="col" className={classes.numeric}>
                {t('documents.attachments.size')}
              </th>
              <th scope="col">
                <span className={classes.srOnly}>{t('documents.viewer.download')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {attachments.map((file) => {
              const kind = file.name.split('.').pop()?.toLowerCase();
              const status = fileStatus?.(file.id);
              return (
                <tr key={file.id}>
                  <td>
                    <span className={classes.fileName}>
                      <FileKindIcon
                        kind={
                          kind === 'pdf' || kind === 'docx' || kind === 'xlsx' || kind === 'png'
                            ? kind
                            : kind === 'jpeg' || kind === 'jpg'
                              ? 'jpg'
                              : 'pdf'
                        }
                        variant="glyph"
                        size={18}
                      />
                      <span title={file.name}>{file.name}</span>
                      {status?.note}
                    </span>
                  </td>
                  <td>{t(`documents.attachments.roles.${file.role}`)}</td>
                  <td className={classes.numeric}>{formatFileSize(file.size, locale)}</td>
                  <td className={classes.actionCell}>
                    <ActionIcon
                      variant="subtle"
                      disabled={!canOutput || status?.available === false}
                      aria-label={t('documents.related.download', { name: file.name })}
                      onClick={() =>
                        onDownload({
                          fileId: file.id,
                          // Eski revizyonun eki durum etiketli (ADR 0028 #30 E; inceleme F3)
                          name: attachmentDownloadName({
                            fileName: file.name,
                            revisionNo: revision.revisionNo,
                            state: revision.state,
                          }),
                        })
                      }
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
